/**
 * Reactive viewport tier flags and range helpers, aligned with Vuetify's breakpoint
 * service (not v-col / v-row props, which use mobile-first "and up" layout semantics).
 *
 * Vuetify 2: delegates to `vuetify.framework.breakpoint`. `xxl` is always false;
 * `xlAndUp` / `xlAndDown` are shimmed until V3 exposes `xxl`.
 *
 * Negated V2 breakpoint checks (do not flip to the wrong *AndDown / *AndUp):
 * `!smAndUp` → `xs`; `!mdAndUp` → `smAndDown` (not `mdAndDown`);
 * `!lgAndUp` → `mdAndDown`; `!xsOnly` → `smAndUp`.
 */
import vuetify from "@/plugins/vuetify";

function breakpoint() {
  return vuetify.framework.breakpoint;
}

export interface ScreenSize {
  readonly xs: boolean;
  readonly sm: boolean;
  readonly md: boolean;
  readonly lg: boolean;
  readonly xl: boolean;
  readonly xxl: boolean;
  readonly smAndDown: boolean;
  readonly smAndUp: boolean;
  readonly mdAndDown: boolean;
  readonly mdAndUp: boolean;
  readonly lgAndDown: boolean;
  readonly lgAndUp: boolean;
  readonly xlAndDown: boolean;
  readonly xlAndUp: boolean;
}

// START OF VUE 2 //
class ScreenSizeV2 implements ScreenSize {
  get xs() {
    return breakpoint().xs;
  }

  get sm() {
    return breakpoint().sm;
  }

  get md() {
    return breakpoint().md;
  }

  get lg() {
    return breakpoint().lg;
  }

  get xl() {
    return breakpoint().xl;
  }

  get xxl() {
    return false;
  }

  get smAndDown() {
    return breakpoint().smAndDown;
  }

  get smAndUp() {
    return breakpoint().smAndUp;
  }

  get mdAndDown() {
    return breakpoint().mdAndDown;
  }

  get mdAndUp() {
    return breakpoint().mdAndUp;
  }

  get lgAndDown() {
    return breakpoint().lgAndDown;
  }

  get lgAndUp() {
    return breakpoint().lgAndUp;
  }

  // No xxl on V2, so every viewport is xl-or-smaller.
  get xlAndDown() {
    return !this.xxl;
  }

  get xlAndUp() {
    return this.xl || this.xxl;
  }
}

export const screenSize: ScreenSize = new ScreenSizeV2();
// END OF VUE 2 //

/* START OF VUE 3 //
import { useDisplay } from "vuetify";

// Implement with useDisplay() inside a composable or reactive wrapper; export a
// singleton that reads the same shape from the active display service.
// END OF VUE 3 */
